import {
  type BenefitPlanGroup,
  formatPolicyPeriodDisplay,
  PlanOptionNameEnum,
} from "@platform/btx-plan-designer";
import {
  type CarrierGroupedGridData,
  formatRequestedColumnLabel,
  isUnmappedPageId,
  type ProposalRatesGridData,
  proposalsToFlatCarrierGridData,
  proposalsToFlatRatesGridData,
  type RequestedBenefitPlanGroup,
} from "@platform/mpx-quotes-grid";
import { useApi } from "@platform/api";
import { useRenewalSchedulesByPlan } from "@platform/btx-rates";
import {
  getCoverageTypeSkeleton,
  useGetCandidatePolicies,
} from "@platform/plan-grid-core";
import { useMemo } from "react";

import {
  REQUESTED_CARRIER_ID,
  REQUESTED_CARRIER_NAME,
} from "../utils/quotesPlanIds";

import type { CoverageTypeEnum } from "@platform/types";
import type { RateSchedule } from "@platform/btx-rates";

// ---------------------------------------------------------------------------
// Pure helpers — build "Requested" carrier column from the selected BPG
// ---------------------------------------------------------------------------

const NO_PLAN_NAME = "-";

/**
 * The RFP's own plans store no name, so loading them fills `name` with the
 * coverage type's generic skeleton default ("Dental PPO Plan"). That default is
 * not the plan's name, and the marketing presentation never prints it, so the
 * Quotes grid never shows it either: the Plan Name row is left empty and the
 * plan's label reads "-".
 */
const isSkeletonDefaultName = (
  name: string | undefined,
  coverage: CarrierGroupedGridData["coverage"] | undefined,
): boolean =>
  coverage !== undefined &&
  name === getCoverageTypeSkeleton(coverage.type).benefitPlans?.[0]?.name;

/**
 * Label for one plan row within the Incumbent/Requested column (distinct from
 * requestedCarrierGroupLabel below, which labels the column's own carrier
 * header), shared with the rates grid so the two name it identically.
 *
 * This plan is the in-force design, not a quote, so it needs no cross-carrier
 * identity of its own — its stored name describes it, and the policy period
 * ("Current") is the fallback when it has none. A skeleton default name (see
 * isSkeletonDefaultName) reads as "-", the grid's empty-cell mark. The RFP
 * identifier is deliberately unused: every carrier quoting this plan resolves
 * to the same one, so it reads as a quote's label rather than this row's.
 */
const requestedPlanLabel = (
  plan: { name?: string },
  policyPeriod: BenefitPlanGroup["policyPeriod"],
  coverage: CarrierGroupedGridData["coverage"] | undefined,
): string => {
  const name = plan.name?.trim();
  if (!name) return formatPolicyPeriodDisplay(policyPeriod);
  return isSkeletonDefaultName(name, coverage) ? NO_PLAN_NAME : name;
};

/** Carrier display name of each candidate policy, keyed by policy UUID. */
type PolicyCarrierNames = ReadonlyMap<string, string>;

const NO_POLICY_CARRIER_NAMES: PolicyCarrierNames = new Map();

/**
 * Resolves a requested source group's incumbent carrier name the way the
 * marketing presentation export does (groupCurrentCarrierName in
 * current_baseline_synthesis.go): the carrier of the group's Current Carrier -
 * Policy selection (ADR 10.22) when it resolves to a name, otherwise the first
 * non-blank legacy free-text `currentCarrierName` among the group's own
 * benefit plans. Only meaningful when the group's policyPeriod is "current" —
 * formatRequestedColumnLabel decides whether it applies.
 */
const resolveCurrentCarrierName = (
  coverage: CarrierGroupedGridData["coverage"] | undefined,
  group: BenefitPlanGroup,
  policyCarrierNames: PolicyCarrierNames,
): string | undefined => {
  const policyCarrierName = group.currentPolicyUuid
    ? policyCarrierNames.get(group.currentPolicyUuid)?.trim()
    : undefined;
  if (policyCarrierName) {
    return policyCarrierName;
  }

  const groupPlanIds = new Set(group.benefitPlans.map((plan) => plan.id));

  return (coverage?.benefitPlans ?? [])
    .filter((plan) => groupPlanIds.has(plan.id))
    .map((plan) => plan.currentCarrierName?.trim())
    .find((name): name is string => Boolean(name));
};

/**
 * The Incumbent/Requested carrier lane's header label for a set of requested
 * source groups: a resolved "<Carrier> - Current" / "Current" / "Requested"
 * label when there's exactly one group, or the generic placeholder when
 * there are several (the Unmapped Plans page splits those apart into their
 * own per-group labels downstream, in buildCarrierGroupedColumns.ts).
 */
const requestedCarrierGroupLabel = (
  coverage: CarrierGroupedGridData["coverage"] | undefined,
  sourceGroups: BenefitPlanGroup[],
  policyCarrierNames: PolicyCarrierNames,
): string => {
  if (sourceGroups.length !== 1) {
    return REQUESTED_CARRIER_NAME;
  }

  const [group] = sourceGroups;
  return formatRequestedColumnLabel(
    resolveCurrentCarrierName(coverage, group, policyCarrierNames),
    group.policyPeriod,
  );
};

const buildRequestedBenefitPlanGroups = (
  coverage: CarrierGroupedGridData["coverage"] | undefined,
  selectedBenefitPlanGroup: BenefitPlanGroup | undefined,
  policyCarrierNames: PolicyCarrierNames,
): RequestedBenefitPlanGroup[] => {
  if (!coverage || !selectedBenefitPlanGroup) {
    return [];
  }

  const selectedPlanIds = new Set(
    selectedBenefitPlanGroup.benefitPlans.map((plan) => plan.id),
  );

  const requestPlans = (coverage.benefitPlans ?? []).filter((plan) =>
    selectedPlanIds.has(plan.id),
  );

  if (requestPlans.length === 0) {
    return [];
  }

  // One multi-plan group, matching the carrier columns' Carrier → Group → Plan
  // shape so header depths stay aligned across the grid.
  return [
    {
      id: `requested-${selectedBenefitPlanGroup.id}`,
      createdAt: selectedBenefitPlanGroup.createdAt,
      updatedAt: selectedBenefitPlanGroup.updatedAt,
      position: 0,
      productType: selectedBenefitPlanGroup.productType,
      policyPeriod: selectedBenefitPlanGroup.policyPeriod,
      nickname: selectedBenefitPlanGroup.nickname,
      // Carried so a multi-group split (splitRequestedCarrierByGroup in
      // buildCarrierGroupedColumns.ts) can resolve each group's own header
      // label independently.
      currentCarrierName: resolveCurrentCarrierName(
        coverage,
        selectedBenefitPlanGroup,
        policyCarrierNames,
      ),
      size: requestPlans.length,
      isRequested: true,
      benefitPlans: requestPlans.map((plan) => {
        const selectedPlanSummary = selectedBenefitPlanGroup.benefitPlans.find(
          (benefitPlan) => benefitPlan.id === plan.id,
        );

        return {
          id: plan.id,
          identifier: requestedPlanLabel(
            plan,
            selectedBenefitPlanGroup.policyPeriod,
            coverage,
          ),
          // requestPlans is filtered to selectedBenefitPlanGroup's own plan
          // ids, so the summary is always found — use this plan's own option.
          optionName: selectedPlanSummary?.optionName,
          eligibilityClasses: plan.eligibilityClasses,
        };
      }),
      coverage: selectedBenefitPlanGroup.coverage,
    },
  ];
};

/**
 * Builds "Requested" reference columns for several source plan groups at
 * once (the Unmapped Plans page shows one reference entry per real requested
 * group, not just the selected page's), renumbering positions so entries
 * from different groups don't collide.
 */
const buildRequestedBenefitPlanGroupsForMany = (
  coverage: CarrierGroupedGridData["coverage"] | undefined,
  sourceGroups: BenefitPlanGroup[],
  policyCarrierNames: PolicyCarrierNames,
): RequestedBenefitPlanGroup[] => {
  let position = 0;
  const result: RequestedBenefitPlanGroup[] = [];
  for (const sourceGroup of sourceGroups) {
    for (const entry of buildRequestedBenefitPlanGroups(
      coverage,
      sourceGroup,
      policyCarrierNames,
    )) {
      result.push({ ...entry, position: position++ });
    }
  }
  return result;
};

const mergeCoverageBenefitPlans = (
  requestCoverage: CarrierGroupedGridData["coverage"] | undefined,
  sourceGroups: BenefitPlanGroup[],
  proposalCoverage: CarrierGroupedGridData["coverage"],
): CarrierGroupedGridData["coverage"] => {
  const mergedPlans = new Map<
    string,
    (typeof proposalCoverage.benefitPlans)[number]
  >();
  const selectedPlanIds = new Set(
    sourceGroups.flatMap((group) => group.benefitPlans.map((plan) => plan.id)),
  );

  (requestCoverage?.benefitPlans ?? [])
    .filter((plan) => selectedPlanIds.has(plan.id))
    .forEach((plan) => {
      mergedPlans.set(
        plan.id,
        isSkeletonDefaultName(plan.name, requestCoverage)
          ? { ...plan, name: undefined }
          : plan,
      );
    });

  (proposalCoverage.benefitPlans ?? []).forEach((plan) => {
    if (!mergedPlans.has(plan.id)) {
      mergedPlans.set(plan.id, plan);
    }
  });

  return {
    ...proposalCoverage,
    benefitPlans: Array.from(mergedPlans.values()),
  };
};

const buildPresentationGridData = (
  carrierGridData: CarrierGroupedGridData | null,
  selectedCoverage: CarrierGroupedGridData["coverage"] | undefined,
  requestedSourceGroups: BenefitPlanGroup[],
  policyCarrierNames: PolicyCarrierNames,
): CarrierGroupedGridData | null => {
  if (!carrierGridData) {
    return null;
  }

  const requestedBenefitPlanGroups = buildRequestedBenefitPlanGroupsForMany(
    selectedCoverage,
    requestedSourceGroups,
    policyCarrierNames,
  );

  if (requestedBenefitPlanGroups.length === 0) {
    return carrierGridData;
  }

  return {
    coverage: mergeCoverageBenefitPlans(
      selectedCoverage,
      requestedSourceGroups,
      carrierGridData.coverage,
    ),
    carrierGroups: [
      {
        carrierId: REQUESTED_CARRIER_ID,
        carrierName: requestedCarrierGroupLabel(
          selectedCoverage,
          requestedSourceGroups,
          policyCarrierNames,
        ),
        groupKey: REQUESTED_CARRIER_ID,
        status: "requested",
        benefitPlanGroups: requestedBenefitPlanGroups,
      },
      ...carrierGridData.carrierGroups,
    ],
    placeholderMaterializeByPlanId:
      carrierGridData.placeholderMaterializeByPlanId,
    padFillByPlanId: carrierGridData.padFillByPlanId,
    emptyBenefitPlanIds: carrierGridData.emptyBenefitPlanIds,
  };
};

// Builds the Requested column for the rates/summary grids as a single group
// keyed by the real backing btx benefit-plan-group id (unlike
// buildRequestedBenefitPlanGroups, which prefixes the id into a synthetic
// "requested-<groupId>" for the SBS plan editor's column identity needs).
// RatesGrid/SummaryGrid already support multi-plan groups, so there's no
// need for that prefix here — and keeping the real id lets SummaryGrid fetch
// this group's premiums from the backend directly.
const buildRequestedRatesBenefitPlanGroups = (
  coverage: CarrierGroupedGridData["coverage"] | undefined,
  selectedBenefitPlanGroup: BenefitPlanGroup | undefined,
  renewalSchedulesByPlan: Record<string, RateSchedule[]>,
): ProposalRatesGridData["benefitPlanGroups"] => {
  if (!coverage || !selectedBenefitPlanGroup) {
    return [];
  }

  const selectedPlanIds = new Set(
    selectedBenefitPlanGroup.benefitPlans.map((plan) => plan.id),
  );

  const requestPlans = (coverage.benefitPlans ?? []).filter((plan) =>
    selectedPlanIds.has(plan.id),
  );

  if (requestPlans.length === 0) {
    return [];
  }

  return [
    {
      id: selectedBenefitPlanGroup.id,
      createdAt: selectedBenefitPlanGroup.createdAt,
      updatedAt: selectedBenefitPlanGroup.updatedAt,
      productType: selectedBenefitPlanGroup.productType,
      policyPeriod: selectedBenefitPlanGroup.policyPeriod,
      position: 0,
      nickname: selectedBenefitPlanGroup.nickname,
      footnote: selectedBenefitPlanGroup.footnote,
      benefitPlanGroupId: selectedBenefitPlanGroup.id,
      size: requestPlans.length,
      benefitPlans: requestPlans.map((plan) => {
        const selectedPlanSummary = selectedBenefitPlanGroup.benefitPlans.find(
          (benefitPlan) => benefitPlan.id === plan.id,
        );

        return {
          id: plan.id,
          identifier: requestedPlanLabel(
            plan,
            selectedBenefitPlanGroup.policyPeriod,
            coverage,
          ),
          type: plan.type ?? selectedBenefitPlanGroup.productType,
          policyPeriod: "current",
          // Prefer this plan's own optionName over another plan's in the
          // group — falling back to selectedBenefitPlanGroup.benefitPlans[0]
          // would misassign e.g. a "high" option's name onto a "low" plan.
          optionName:
            plan.optionName ??
            selectedPlanSummary?.optionName ??
            PlanOptionNameEnum.NOT_APPLICABLE,
          // The renewal-inclusive read (current + renewal + revisedRenewal) is the
          // source when it resolved; the coverage payload's current-only schedules
          // are the fallback while that read is in flight.
          rateSchedule:
            renewalSchedulesByPlan[plan.id] ??
            (plan.rateSchedule as unknown as RateSchedule[] | undefined),
          benefits: plan.benefits as Record<string, unknown> | undefined,
        };
      }),
    },
  ];
};

// A rates plan synthesized for a benefit plan that has no matching rate
// schedule for this carrier (e.g. the carrier never quoted this plan).
// Carries an empty rateSchedule rather than omitting the plan entirely, so
// RatesGrid can still render a cell for it — one the analyst can fill in to
// create the plan's first real rate schedule.
const buildPlaceholderRatesBenefitPlan = (
  plan: BenefitPlanGroup["benefitPlans"][number],
  group: BenefitPlanGroup,
): ProposalRatesGridData["benefitPlanGroups"][number]["benefitPlans"][number] => ({
  id: plan.id,
  identifier: plan.identifier,
  type: group.productType,
  policyPeriod: "current",
  optionName: plan.optionName ?? PlanOptionNameEnum.NOT_APPLICABLE,
  rateScheduleSource: "proposal",
  rateSchedule: [],
});

// A rates plan group synthesized when the carrier grid has a plan group that
// the rates data doesn't cover at all — every plan gets an empty rate cell.
const buildPlaceholderRatesBenefitPlanGroup = (
  group: BenefitPlanGroup,
  benefitPlans: ProposalRatesGridData["benefitPlanGroups"][number]["benefitPlans"],
  position: number,
): ProposalRatesGridData["benefitPlanGroups"][number] => ({
  id: `placeholder-rate-group-${group.id}`,
  productType: group.productType,
  policyPeriod: "current",
  position,
  nickname: group.nickname ?? benefitPlans[0]?.identifier,
  footnote: group.footnote,
  proposalPlanGroupId: group.isPlaceholder
    ? undefined
    : (group.proposalPlanGroupId ?? group.id),
  size: benefitPlans.length,
  isProposalGroup: true,
  benefitPlans,
});

/**
 * Builds "Requested" reference rates columns for several source plan groups
 * at once, mirroring buildRequestedBenefitPlanGroupsForMany above so the
 * rates/summary grids get the same set of Requested columns as the plan
 * editor grid on the Unmapped Plans page.
 */
const buildRequestedRatesBenefitPlanGroupsForMany = (
  coverage: CarrierGroupedGridData["coverage"] | undefined,
  sourceGroups: BenefitPlanGroup[],
  renewalSchedulesByPlan: Record<string, RateSchedule[]>,
): ProposalRatesGridData["benefitPlanGroups"] => {
  let position = 0;
  const result: ProposalRatesGridData["benefitPlanGroups"] = [];
  for (const sourceGroup of sourceGroups) {
    for (const entry of buildRequestedRatesBenefitPlanGroups(
      coverage,
      sourceGroup,
      renewalSchedulesByPlan,
    )) {
      result.push({ ...entry, position: position++ });
    }
  }
  return result;
};

// Proposals carry dental HMO/PPO plans mixed under one "dental" coverage
// (see coverageTypeNormalizer.ts), so rates data built independently from
// proposals can include plans the main carrier grid excludes for the
// selected dental LOC. It can also be missing plans the carrier grid
// includes: a carrier's proposal has no rate schedule at all for a plan it
// never quoted. Rebuild each carrier's rates plan groups from the carrier
// grid's own group-ordered structure — the single source of truth for which
// groups/plans exist and in what order — reusing the matching rates group's
// metadata and per-plan rate schedules, and injecting empty placeholders
// where a group or plan has no rate data, so every carrier's rates columns
// line up with the plan editor above them.
export const alignRatesGridDataWithCarrierGrid = (
  ratesGridData: ProposalRatesGridData | null,
  carrierGridData: CarrierGroupedGridData | null,
): ProposalRatesGridData | null => {
  // The carrier grid found no plans for the selected LOC (e.g. the dental
  // filter above excluded every plan) — there's nothing for Rates to show.
  if (!carrierGridData) {
    return null;
  }

  const ratesCarrierGroupById = new Map(
    (ratesGridData?.carrierGroups ?? []).map((carrierGroup) => [
      carrierGroup.carrierId,
      carrierGroup,
    ]),
  );

  const carrierGroups = carrierGridData.carrierGroups.map((carrierGroup) => {
    const ratesCarrierGroup = ratesCarrierGroupById.get(carrierGroup.carrierId);
    const ratesGroupByPlanId = new Map(
      (ratesCarrierGroup?.benefitPlanGroups ?? []).flatMap((group) =>
        (group.benefitPlans ?? []).map((plan) => [plan.id, group] as const),
      ),
    );
    const ratesPlanById = new Map(
      (ratesCarrierGroup?.benefitPlanGroups ?? []).flatMap((group) =>
        (group.benefitPlans ?? []).map((plan) => [plan.id, plan] as const),
      ),
    );

    let position = 0;
    const benefitPlanGroups = carrierGroup.benefitPlanGroups.map((group) => {
      const plans = group.benefitPlans ?? [];
      const benefitPlans = plans.map(
        (plan) =>
          ratesPlanById.get(plan.id) ??
          buildPlaceholderRatesBenefitPlan(plan, group),
      );
      // Reuse the source rates group's metadata (real id, nickname, premiums)
      // when any of this group's plans has one; otherwise the whole group is a
      // placeholder. Plans in one carrier-grid group share a rates group, so
      // the first match is representative.
      const sourceRatesGroup = plans
        .map((plan) => ratesGroupByPlanId.get(plan.id))
        .find((ratesGroup) => ratesGroup != null);

      return sourceRatesGroup
        ? {
            ...sourceRatesGroup,
            position: position++,
            size: benefitPlans.length,
            benefitPlans,
          }
        : buildPlaceholderRatesBenefitPlanGroup(
            group,
            benefitPlans,
            position++,
          );
    });

    return {
      carrierId: carrierGroup.carrierId,
      groupKey: carrierGroup.groupKey,
      carrierName: carrierGroup.carrierName,
      benefitPlanGroups,
    };
  });

  return {
    carrierGroups,
    benefitPlanGroups: carrierGroups.flatMap(
      (carrierGroup) => carrierGroup.benefitPlanGroups,
    ),
  };
};

const buildPresentationRatesGridData = (
  ratesGridData: ProposalRatesGridData | null,
  selectedCoverage: CarrierGroupedGridData["coverage"] | undefined,
  requestedSourceGroups: BenefitPlanGroup[],
  renewalSchedulesByPlan: Record<string, RateSchedule[]>,
  policyCarrierNames: PolicyCarrierNames,
): ProposalRatesGridData | null => {
  const requestedBenefitPlanGroups =
    buildRequestedRatesBenefitPlanGroupsForMany(
      selectedCoverage,
      requestedSourceGroups,
      renewalSchedulesByPlan,
    );
  const requestedCarrierName = requestedCarrierGroupLabel(
    selectedCoverage,
    requestedSourceGroups,
    policyCarrierNames,
  );

  if (!ratesGridData) {
    if (requestedBenefitPlanGroups.length === 0) {
      return null;
    }
    return {
      benefitPlanGroups: requestedBenefitPlanGroups,
      carrierGroups: [
        {
          carrierId: REQUESTED_CARRIER_ID,
          groupKey: REQUESTED_CARRIER_ID,
          carrierName: requestedCarrierName,
          benefitPlanGroups: requestedBenefitPlanGroups,
        },
      ],
    };
  }

  if (requestedBenefitPlanGroups.length === 0) {
    return ratesGridData;
  }

  return {
    benefitPlanGroups: [
      ...requestedBenefitPlanGroups,
      ...ratesGridData.benefitPlanGroups,
    ],
    carrierGroups: [
      {
        carrierId: REQUESTED_CARRIER_ID,
        groupKey: REQUESTED_CARRIER_ID,
        carrierName: requestedCarrierName,
        benefitPlanGroups: requestedBenefitPlanGroups,
      },
      ...ratesGridData.carrierGroups,
    ],
  };
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Carrier names for the coverage's candidate policies, fetched only when a
 * requested group has a Current Carrier - Policy selected. Reads the policy
 * picker's query (useGetCandidatePolicies) with the app's client, since this
 * runs outside PlanDesignerProvider. Fetched without a plan-type filter, so one
 * list holds every group's selection. A failed lookup leaves the map empty,
 * and the header falls back to the plans' free-text carrier name, as the
 * export does.
 */
const usePolicyCarrierNames = (
  coverageId: string | undefined,
  requestedSourceGroups: BenefitPlanGroup[],
): PolicyCarrierNames => {
  const axiosInstance = useApi();
  const hasPolicySelection = requestedSourceGroups.some(
    (group) => group.currentPolicyUuid,
  );

  const { data: policies } = useGetCandidatePolicies(
    coverageId ?? "",
    undefined,
    { enabled: Boolean(coverageId) && hasPolicySelection },
    axiosInstance,
  );

  return useMemo(
    () =>
      policies
        ? new Map(
            policies.map((policy) => [policy.policyUuid, policy.carrierName]),
          )
        : NO_POLICY_CARRIER_NAMES,
    [policies],
  );
};

interface QuotesPresentationGridDataParams {
  proposals: Parameters<typeof proposalsToFlatCarrierGridData>[0];
  selectedCoverageType: CoverageTypeEnum | undefined;
  selectedBpgId: string | undefined;
  carrierIdOrderForGrid: string[] | undefined;
  selectedCoverage: CarrierGroupedGridData["coverage"] | undefined;
  selectedBenefitPlanGroup: BenefitPlanGroup | undefined;
  /**
   * All real requested plan groups for the selected coverage (excludes the
   * synthetic Unmapped Plans page entry). Used only on the Unmapped Plans
   * page, where every real group's plans render as read-only reference
   * columns instead of just the selected page's.
   */
  benefitPlanGroupsForCoverage: BenefitPlanGroup[];
}

interface QuotesPresentationGridData {
  carrierGridData: CarrierGroupedGridData | null;
  ratesGridData: ProposalRatesGridData | null;
}

/**
 * Computes the presentation-layer grid data for the Quotes tab by:
 * 1. Building raw carrier/rates data from proposals
 * 2. Injecting "Requested" reference column(s) — the selected page's group,
 *    or (on the Unmapped Plans page) every real requested group — into both
 *    the carrier and rates grids, so their column groups stay aligned. A
 *    current group's header names its Current Carrier - Policy carrier,
 *    fetched from the coverage's candidate policies, before the plans'
 *    free-text carrier name (resolveCurrentCarrierName)
 * 3. Merging coverage benefit plans
 */
export const useQuotesPresentationGridData = ({
  proposals,
  selectedCoverageType,
  selectedBpgId,
  carrierIdOrderForGrid,
  selectedCoverage,
  selectedBenefitPlanGroup,
  benefitPlanGroupsForCoverage,
}: QuotesPresentationGridDataParams): QuotesPresentationGridData => {
  const axiosInstance = useApi();
  const isUnmappedPage = isUnmappedPageId(selectedBpgId);

  const requestedSourceGroups = useMemo(
    () =>
      isUnmappedPage
        ? benefitPlanGroupsForCoverage
        : selectedBenefitPlanGroup
          ? [selectedBenefitPlanGroup]
          : [],
    [isUnmappedPage, benefitPlanGroupsForCoverage, selectedBenefitPlanGroup],
  );

  // The requested/incumbent column is the only one that can carry renewal rates;
  // fetch its plans' renewal-inclusive schedules so the grid can show the renewal
  // column beside current.
  const requestedPlanIds = useMemo(
    () =>
      requestedSourceGroups.flatMap((group) =>
        group.benefitPlans.map((plan) => plan.id),
      ),
    [requestedSourceGroups],
  );
  const renewalSchedulesByPlan = useRenewalSchedulesByPlan(
    requestedPlanIds,
    axiosInstance,
  );
  const policyCarrierNames = usePolicyCarrierNames(
    selectedCoverage?.id,
    requestedSourceGroups,
  );

  const rawCarrierGridData: CarrierGroupedGridData | null = useMemo(
    () =>
      selectedCoverageType
        ? proposalsToFlatCarrierGridData(
            proposals,
            selectedCoverageType,
            selectedBpgId,
            carrierIdOrderForGrid,
            selectedCoverage,
          )
        : null,
    [
      proposals,
      selectedCoverageType,
      selectedBpgId,
      carrierIdOrderForGrid,
      selectedCoverage,
    ],
  );

  const carrierGridData = useMemo(
    () =>
      buildPresentationGridData(
        rawCarrierGridData,
        selectedCoverage,
        requestedSourceGroups,
        policyCarrierNames,
      ),
    [
      rawCarrierGridData,
      selectedCoverage,
      requestedSourceGroups,
      policyCarrierNames,
    ],
  );

  const rawRatesGridData = useMemo(
    () =>
      selectedCoverageType
        ? proposalsToFlatRatesGridData(
            proposals,
            selectedCoverageType,
            selectedBpgId,
            carrierIdOrderForGrid,
          )
        : null,
    [proposals, selectedCoverageType, selectedBpgId, carrierIdOrderForGrid],
  );

  const alignedRatesGridData = useMemo(
    () =>
      alignRatesGridDataWithCarrierGrid(rawRatesGridData, rawCarrierGridData),
    [rawRatesGridData, rawCarrierGridData],
  );

  const ratesGridData = useMemo(
    () =>
      buildPresentationRatesGridData(
        alignedRatesGridData,
        selectedCoverage,
        requestedSourceGroups,
        renewalSchedulesByPlan,
        policyCarrierNames,
      ),
    [
      alignedRatesGridData,
      selectedCoverage,
      requestedSourceGroups,
      renewalSchedulesByPlan,
      policyCarrierNames,
    ],
  );

  return { carrierGridData, ratesGridData };
};

export { REQUESTED_CARRIER_ID, REQUESTED_CARRIER_NAME };
